import React, { useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SectionHeader } from "@/components/common/Widgets";
import { Upload, Download, CheckCircle2, XCircle, AlertTriangle, FileSpreadsheet, Loader2 } from "lucide-react";

export default function LegacyUploadPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setPreview(null); if (inputRef.current) inputRef.current.value = ""; };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("acas_token");
      const r = await fetch(`${API}/customers/legacy/template`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "legacy_customers_template.csv"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) { toast.error("Download failed"); }
  };

  const onFile = (e) => { setFile(e.target.files?.[0] || null); setPreview(null); };

  const doPreview = async () => {
    if (!file) return toast.error("Pick a CSV/Excel first");
    setPreviewing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/customers/legacy/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      toast.success(`Preview: ${r.data.inserted_count} new, ${r.data.rejected_count} duplicates, ${r.data.error_count} errors`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setPreviewing(false); }
  };

  const doCommit = async () => {
    if (!file) return toast.error("Pick a CSV/Excel first");
    if (!window.confirm(`Commit upload? ${preview?.inserted_count ?? "?"} customers will be inserted. ${preview?.rejected_count ?? "?"} duplicates rejected.`)) return;
    setCommitting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/customers/legacy/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      toast.success(`Imported — ${r.data.inserted_count} new customers added, ${r.data.rejected_count} duplicates skipped`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setCommitting(false); }
  };

  return (
    <div className="space-y-5" data-testid="legacy-upload-page">
      <SectionHeader
        title="Legacy Customer Data Upload"
        subtitle="Bulk-import customers from your old core-banking system. Rows whose customer number (mobile) already exists in this tenant are REJECTED — remaining rows are imported."
        action={(
          <Button variant="secondary" onClick={downloadTemplate} data-testid="legacy-template-download"><Download className="h-4 w-4 mr-1" />Template CSV</Button>
        )}
      />

      <Card className="p-5">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="text-sm flex-1" data-testid="legacy-file-input" />
          {file && (
            <div className="text-xs text-muted-foreground flex items-center gap-1.5"><FileSpreadsheet className="h-3.5 w-3.5" />{file.name} · {(file.size / 1024).toFixed(1)} KB</div>
          )}
          <div className="flex gap-2">
            {file && <Button variant="ghost" size="sm" onClick={reset}>Reset</Button>}
            <Button variant="outline" size="sm" disabled={!file || previewing} onClick={doPreview} data-testid="legacy-preview">{previewing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}Preview</Button>
            <Button size="sm" disabled={!file || committing || !preview} onClick={doCommit} data-testid="legacy-commit">{committing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}Commit Upload</Button>
          </div>
        </div>
      </Card>

      {preview && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard color="success" icon={CheckCircle2} label="Will Insert (or Inserted)" value={preview.inserted_count} />
          <StatCard color="warning" icon={AlertTriangle} label="Rejected (Duplicate Customer Number)" value={preview.rejected_count} />
          <StatCard color="destructive" icon={XCircle} label="Errors (Validation)" value={preview.error_count} />
        </div>
      )}

      {preview && preview.inserted?.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between"><div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-success" /><span className="text-sm font-semibold heading">Will be inserted</span></div><Badge variant="outline" className="text-[10px]">{preview.inserted.length}</Badge></div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground sticky top-0"><tr><th className="text-left px-3 py-2">Row</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Mobile</th><th className="text-left px-3 py-2">Customer ID</th></tr></thead>
              <tbody>{preview.inserted.map(r => <tr key={r.row} className="border-t border-border"><td className="px-3 py-1.5 mono text-xs">{r.row}</td><td className="px-3 py-1.5">{r.name}</td><td className="px-3 py-1.5 mono text-xs">{r.mobile}</td><td className="px-3 py-1.5 mono text-xs text-muted-foreground">{r.id}</td></tr>)}</tbody>
            </table>
          </div>
        </Card>
      )}

      {preview && preview.rejected?.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between bg-warning/5"><div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-warning" /><span className="text-sm font-semibold heading">Rejected — duplicate customer number</span></div><Badge variant="outline" className="text-[10px] border-warning/40 text-warning">{preview.rejected.length}</Badge></div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground sticky top-0"><tr><th className="text-left px-3 py-2">Row</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Mobile</th><th className="text-left px-3 py-2">Reason</th></tr></thead>
              <tbody>{preview.rejected.map(r => <tr key={r.row} className="border-t border-border"><td className="px-3 py-1.5 mono text-xs">{r.row}</td><td className="px-3 py-1.5">{r.name || "—"}</td><td className="px-3 py-1.5 mono text-xs">{r.mobile}</td><td className="px-3 py-1.5 text-xs">{r.reason}</td></tr>)}</tbody>
            </table>
          </div>
        </Card>
      )}

      {preview && preview.errors?.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between bg-destructive/5"><div className="flex items-center gap-2"><XCircle className="h-4 w-4 text-destructive" /><span className="text-sm font-semibold heading">Errors</span></div><Badge variant="outline" className="text-[10px] border-destructive/40 text-destructive">{preview.errors.length}</Badge></div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground sticky top-0"><tr><th className="text-left px-3 py-2">Row</th><th className="text-left px-3 py-2">Reason</th></tr></thead>
              <tbody>{preview.errors.map(r => <tr key={r.row} className="border-t border-border"><td className="px-3 py-1.5 mono text-xs">{r.row}</td><td className="px-3 py-1.5 text-xs text-destructive">{r.reason}</td></tr>)}</tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function StatCard({ color, icon: Icon, label, value }) {
  const map = { success: "text-success border-success/30 bg-success/5", warning: "text-warning border-warning/30 bg-warning/5", destructive: "text-destructive border-destructive/30 bg-destructive/5" };
  return (
    <Card className={`p-4 border ${map[color]}`}>
      <div className="flex items-center justify-between"><Icon className="h-5 w-5" /><span className="text-3xl font-semibold tabular">{value}</span></div>
      <div className="text-xs mt-1 uppercase tracking-wide font-medium">{label}</div>
    </Card>
  );
}
